export type HomeSection =
  | 'feed'
  | 'requests'
  | 'mail'
  | 'activity'
  | 'random'
  | 'friends-of-friends'
  | 'new'
  | 'people-like-you';

export type SkyspaceRoute =
  | {
      type: 'profile';
      handle: string;
      postRkey?: string;
      postCollection?: 'app.bsky.feed.post' | 'me.skyspace.profile.comment';
      canonicalize: boolean;
    }
  | {
      type: 'home';
      section: HomeSection;
      postHandle?: string;
      postRkey?: string;
    }
  | {
      type: 'search';
      query: string;
      onlyInterests: boolean;
      searchType: 'skyspace' | 'users';
    }
  | { type: 'oauth-callback' };

export function parseLegacyRoute(search: string, hash = ''): SkyspaceRoute {
  const query = String(search || '').replace(/^\?/, '');
  const params = new URLSearchParams(query);

  if (params.has('home')) {
    const requested = params.get('home');
    const postHandle = (params.get('profile') || '').trim();
    const postRkey = /^[A-Za-z0-9._~:-]{1,512}$/.test(params.get('post') || '')
      ? params.get('post') || undefined
      : undefined;
    const sections = new Set<HomeSection>([
      'feed',
      'requests',
      'mail',
      'activity',
      'random',
      'friends-of-friends',
      'new',
      'people-like-you',
    ]);
    return {
      type: 'home',
      section: sections.has(requested as HomeSection) ? requested as HomeSection : 'feed',
      ...(postHandle && postRkey ? { postHandle, postRkey } : {}),
    };
  }
  if (params.has('search')) {
    return {
      type: 'search',
      query: params.get('search')?.trim() || '',
      onlyInterests: params.get('onlyInterests') === 'true',
      searchType: params.get('searchType') === 'users' ? 'users' : 'skyspace',
    };
  }
  if (!query && hash.includes('code=')) {
    return { type: 'oauth-callback' };
  }

  let handle = params.has('profile') ? params.get('profile') || '' : query;
  try {
    handle = decodeURIComponent(handle).trim();
  } catch {
    handle = handle.trim();
  }

  return {
    type: 'profile',
    handle: handle || 'skyspace.me',
    postRkey: params.has('profile') && /^[A-Za-z0-9._~:-]{1,512}$/.test(params.get('post') || '')
      ? params.get('post') || undefined
      : undefined,
    postCollection: params.has('post')
      ? params.get('collection') === 'me.skyspace.profile.comment'
        ? 'me.skyspace.profile.comment'
        : 'app.bsky.feed.post'
      : undefined,
    canonicalize: handle === '',
  };
}

export function profileSearch(handle: string): string {
  return `?${encodeURIComponent(handle)}`;
}

export function postSearch(handle: string, postUriOrRkey: string): string {
  const parts = postUriOrRkey.split('/');
  const rkey = parts.pop() || '';
  const collection = parts.pop();
  return `?profile=${encodeURIComponent(handle)}&post=${encodeURIComponent(rkey)}${
    collection === 'me.skyspace.profile.comment'
      ? '&collection=me.skyspace.profile.comment'
      : ''
  }`;
}

export function searchSearch(
  query: string,
  searchType: 'skyspace' | 'users' = 'skyspace',
): string {
  return `?search=${encodeURIComponent(query)}${searchType === 'users' ? '&searchType=users' : ''}`;
}

export function homeSearch(section = 'feed'): string {
  return `?home=${encodeURIComponent(section)}`;
}

export function homePostSearch(handle: string, postUriOrRkey: string): string {
  const rkey = postUriOrRkey.split('/').pop() || '';
  return `?home=feed&profile=${encodeURIComponent(handle)}&post=${encodeURIComponent(rkey)}`;
}
